import {areSetsEqual, groupByUniqueKey} from '@benchling/js-utils';
import isEqual from 'react-fast-compare';

import areProcessInteractiveFlowchartsDifferent from 'processes/graph-builder-processes/flowchart-viz/util/areProcessInteractiveFlowchartsDifferent';

import {
  ClientFlowchartConfig,
  isPlaceholderTaskSchema,
  ClientSchemaMapperConfig,
} from './FlowchartConfigConstants';

export function areSchemaMapperConfigListsDifferent(
  clientSchemaMapperConfigsA: Array<ClientSchemaMapperConfig>,
  clientSchemaMapperConfigsB: Array<ClientSchemaMapperConfig>
): boolean {
  if (
    !areSetsEqual(
      new Set(clientSchemaMapperConfigsA.map(({clientId}) => clientId)),
      new Set(clientSchemaMapperConfigsB.map(({clientId}) => clientId))
    )
  ) {
    return true;
  }

  const clientSchemaMapperConfigsAByClientId = groupByUniqueKey(
    clientSchemaMapperConfigsA,
    ({clientId}) => clientId
  );

  for (const schemaMapperConfig of clientSchemaMapperConfigsB) {
    if (!isEqual(schemaMapperConfig, clientSchemaMapperConfigsAByClientId[schemaMapperConfig.clientId])) {
      return true;
    }
  }

  return false;
}

/**
 * Determines whether or not the provided client flowchart configs are meaningfully different.
 *
 * Flowcharts are considered to be "the same" if the objects are deeply equal or the only difference between
 * them is:
 * - The order of the `availableTaskSchemas`
 * - The order of the `schemaMapperConfigs`
 * - The positions of nodes or edges in the `templateFlowchart`
 *
 * @param clientFlowchartConfigA The first client flowchart config
 * @param clientFlowchartConfigB The second client flowchart config
 * @returns Whether or not the client flowchart configs are meaningfully different.
 */
export default function areClientFlowchartConfigsDifferent(
  clientFlowchartConfigA: ClientFlowchartConfig,
  clientFlowchartConfigB: ClientFlowchartConfig
): boolean {
  if (
    clientFlowchartConfigA.isFlowchartModifiableOnCreation !==
      clientFlowchartConfigB.isFlowchartModifiableOnCreation ||
    clientFlowchartConfigA.isFlowchartModifiableAfterCreation !==
      clientFlowchartConfigB.isFlowchartModifiableAfterCreation
  ) {
    return true;
  }

  const getTaskSchemaIdsSet = (flowchartConfig: ClientFlowchartConfig): Set<string> =>
    new Set(
      flowchartConfig.availableTaskSchemas.map((taskSchema) =>
        isPlaceholderTaskSchema(taskSchema) ? taskSchema.placeholderClientId : taskSchema.id
      )
    );

  if (
    !areSetsEqual(getTaskSchemaIdsSet(clientFlowchartConfigA), getTaskSchemaIdsSet(clientFlowchartConfigB))
  ) {
    return true;
  }

  if (
    areSchemaMapperConfigListsDifferent(
      clientFlowchartConfigA.schemaMapperConfigs,
      clientFlowchartConfigB.schemaMapperConfigs
    )
  ) {
    return true;
  }

  return areProcessInteractiveFlowchartsDifferent(
    clientFlowchartConfigA.templateFlowchart,
    clientFlowchartConfigB.templateFlowchart
  );
}
